<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';

import { ref, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

import type { SettingsBreadcrumb, SettingsNavigationConfig } from './settings-layout-types';

import { AppButton } from '../button';
import { IconChevronLeft, IconChevronRight } from '../icons';
import SettingsNavigation from './SettingsNavigation.vue';

const props = defineProps<{
  backTo?: RouteLocationRaw;
  breadcrumbs?: SettingsBreadcrumb[];
  navigation?: SettingsNavigationConfig;
  contentKey?: string;
}>();
const emit = defineEmits<{ select: [id: string] }>();
const router = useRouter();
const scroller = ref<HTMLElement | null>(null);

watch(
  () => props.contentKey,
  () => {
    if (scroller.value) scroller.value.scrollTop = 0;
  },
  { flush: 'post' },
);
</script>

<template>
  <div
    ref="scroller"
    class="min-h-0 min-w-0 flex-1 overflow-y-auto py-4 text-(--base-text-color) sm:py-6"
  >
    <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-8 pb-8">
      <div
        v-if="backTo || breadcrumbs?.length"
        class="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-3"
      >
        <AppButton
          v-if="backTo"
          outline
          type="button"
          class="min-h-11 shrink-0 gap-2 rounded-full border border-(--base-border-color) bg-transparent hover:bg-(--secondary-button-hover-bg-color)"
          @click="router.push(backTo)"
        >
          <IconChevronLeft
            :width="8"
            :height="14"
            aria-hidden="true"
          />
          Назад
        </AppButton>
        <nav
          v-if="breadcrumbs?.length"
          aria-label="Хлебные крошки"
          class="min-w-0"
        >
          <ol class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-(--base-secondary-text-color)">
            <li
              v-for="(crumb, index) in breadcrumbs"
              :key="index"
              class="flex min-w-0 items-center gap-3"
            >
              <IconChevronRight
                v-if="index"
                :width="5"
                :height="9"
                aria-hidden="true"
                class="shrink-0"
              />
              <RouterLink
                v-if="crumb.to && index < breadcrumbs.length - 1"
                :to="crumb.to"
                class="inline-flex min-h-11 min-w-0 items-center rounded break-words hover:text-(--base-text-color) focus-visible:outline-2 focus-visible:outline-(--base-accent-color)"
                >{{ crumb.label }}</RouterLink
              >
              <span
                v-else
                :aria-current="index === breadcrumbs.length - 1 ? 'page' : undefined"
                class="min-w-0 break-words"
                :class="{ 'text-(--base-text-color)': index === breadcrumbs.length - 1 }"
                >{{ crumb.label }}</span
              >
            </li>
          </ol>
        </nav>
      </div>
      <div
        :class="
          navigation
            ? 'grid min-w-0 items-start gap-6 xl:grid-cols-[320px_minmax(0,1fr)] xl:gap-8 2xl:grid-cols-[360px_minmax(0,1fr)] 2xl:gap-10'
            : 'min-w-0'
        "
      >
        <SettingsNavigation
          v-if="navigation"
          :navigation="navigation"
          @select="emit('select', $event)"
        />
        <div class="min-w-0"><slot /></div>
      </div>
    </div>
  </div>
</template>
